import { useState, useEffect, useRef } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Volume2,
  ArrowRight,
  Plus,
  ChevronLeft,
  ChevronRight,
  PhoneCall,
  Calendar,
  FileSpreadsheet,
} from 'lucide-react';
import { getAuthLink } from '@/lib/links';
import { HOME_AGENTS, type HomeAgent } from './homeData';

export function AgentFleetMonday() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedAgentId, setSelectedAgentId] = useState<string>('ashley');
  const [activeVoiceAgent, setActiveVoiceAgent] = useState<string>('');
  const [carouselOffset, setCarouselOffset] = useState<number>(0);

  const selectedCardRef = useRef<HTMLDivElement>(null);
  const focusSelectedRef = useRef(false);

  const currentAgent = HOME_AGENTS.find((a) => a.id === selectedAgentId) || HOME_AGENTS[0];

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (focusSelectedRef.current) {
      focusSelectedRef.current = false;
      selectedCardRef.current?.focus();
    }
  }, [selectedAgentId]);

  const selectAgent = (id: string, moveFocus = false) => {
    focusSelectedRef.current = moveFocus;
    setSelectedAgentId(id);
    setCarouselOffset(0);
    setActiveVoiceAgent('');
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  };

  const handleVoicePreview = (agent: HomeAgent) => {
    if (activeVoiceAgent === agent.id) {
      setActiveVoiceAgent('');
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      return;
    }

    setActiveVoiceAgent(agent.id);
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(agent.summary);
      utterance.rate = 0.95;
      const clear = () => setActiveVoiceAgent((cur) => (cur === agent.id ? '' : cur));
      utterance.onend = clear;
      utterance.onerror = clear;
      window.speechSynthesis.speak(utterance);
    }
  };

  // Reorder cards so the selected agent is always first in the 3-card showcase
  const orderedAgents = [currentAgent, ...HOME_AGENTS.filter((a) => a.id !== currentAgent.id)];
  const visibleCards = [
    orderedAgents[carouselOffset % orderedAgents.length],
    orderedAgents[(carouselOffset + 1) % orderedAgents.length],
    orderedAgents[(carouselOffset + 2) % orderedAgents.length],
  ];

  return (
    <section className="py-20 sm:py-24 bg-white">
      <div className="max-w-[1380px] mx-auto px-5 sm:px-8">
        {/* Exact monday.com Slice 03 Section Heading: 56px Poppins Regular */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <h2 className="text-4xl sm:text-[48px] lg:text-[56px] font-normal tracking-[-0.03em] text-[#000000] leading-[1.12]">
            An agent for every job
          </h2>
        </motion.div>

        {/* Exact monday.com Slice 03 Stage: Rounded Light-Gray Container (#F3F4F5) */}
        <div className="bg-[#F3F4F5] rounded-[36px] p-6 sm:p-10 lg:p-12">
          {/* Top Carousel Navigation Arrows */}
          <div className="flex justify-end items-center gap-2 mb-6">
            <button
              type="button"
              aria-label="Previous agent cards"
              onClick={() =>
                setCarouselOffset(
                  (prev) => (prev - 1 + orderedAgents.length) % orderedAgents.length
                )
              }
              className="size-11 rounded-full bg-white/80 hover:bg-white border border-gray-200/80 flex items-center justify-center text-[#181B20] transition-colors shadow-2xs"
            >
              <ChevronLeft className="size-4" />
            </button>
            <button
              type="button"
              aria-label="Next agent cards"
              onClick={() => setCarouselOffset((prev) => (prev + 1) % orderedAgents.length)}
              className="size-11 rounded-full bg-white hover:bg-gray-50 border border-gray-200/80 flex items-center justify-center text-[#181B20] transition-colors shadow-2xs"
            >
              <ChevronRight className="size-4" />
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Left Column (4 cols): Expanded White Card for Active Agent + Compact (+) Pill Buttons for Others */}
            <div className="lg:col-span-4 flex flex-col gap-3" aria-label="AI Agent roster">
              {HOME_AGENTS.map((agent) => {
                const isSelected = agent.id === currentAgent.id;
                const isPlayingVoice = activeVoiceAgent === agent.id;

                if (isSelected) {
                  return (
                    <motion.div
                      key={agent.id}
                      layout
                      ref={selectedCardRef}
                      tabIndex={-1}
                      aria-current="true"
                      className="bg-white rounded-[24px] p-6 shadow-[0_8px_30px_rgba(0,0,0,0.04)] border border-gray-100"
                    >
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <h3 className="text-lg font-semibold text-[#000000]">
                          <span>{agent.name}</span>
                          <span> · {agent.role}</span>
                        </h3>
                        <span className="text-[11px] font-medium text-[#7950A2] bg-[#E5DEEE] px-2.5 py-0.5 rounded-full">
                          {agent.job}
                        </span>
                      </div>

                      <p className="text-xs sm:text-[13px] text-[#323338] leading-relaxed mb-4">
                        {agent.summary}
                      </p>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleVoicePreview(agent)}
                          className={`inline-flex min-h-11 items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all border ${
                            isPlayingVoice
                              ? 'bg-[#7950A2] text-white border-[#7950A2]'
                              : 'bg-[#F3F4F5] text-[#181B20] border-gray-200 hover:bg-[#E5DEEE]/60'
                          }`}
                        >
                          <Volume2 className="size-3.5" />
                          <span>{isPlayingVoice ? 'Playing Voice Sample...' : 'Sample Voice'}</span>
                          {isPlayingVoice && !shouldReduceMotion && (
                            <span className="inline-block size-2 rounded-full bg-[#F7C76C] animate-ping" />
                          )}
                        </button>
                      </div>
                    </motion.div>
                  );
                }

                return (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => selectAgent(agent.id, true)}
                    className="group w-full rounded-full border border-gray-300/90 bg-transparent hover:bg-white/80 px-4 py-3 text-left flex items-center gap-3 transition-all"
                  >
                    <span className="size-6 rounded-full border border-gray-400/80 group-hover:border-[#7950A2] group-hover:text-[#7950A2] flex items-center justify-center text-[#323338] shrink-0 transition-colors">
                      <Plus className="size-3.5" />
                    </span>
                    <span className="text-sm font-normal text-[#181B20] truncate">
                      <span className="font-medium">{agent.name}</span>
                      <span className="text-[#676879]"> · {agent.role}</span>
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Right Column (8 cols): Exact monday.com Slice 03 3-Card Horizontal Showcase */}
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-5">
              {visibleCards.map((agent) => (
                <motion.div
                  key={agent.id}
                  layout
                  whileHover={{ y: -4 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 26 }}
                  onClick={() => selectAgent(agent.id)}
                  className="cursor-pointer rounded-[28px] bg-white p-4 shadow-[0_10px_35px_rgba(0,0,0,0.04)] border border-gray-100 flex flex-col justify-between"
                >
                  <div>
                    {/* Square Portrait (No overlaid pills on images per taste-skill anti-slop rules) */}
                    <div className="aspect-square rounded-2xl overflow-hidden bg-[#E5DEEE]/50 mb-3">
                      <img
                        src={agent.portrait}
                        alt={agent.name}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>

                    {/* Clean Status Note Strip Below Portrait */}
                    <div className="mb-2.5 px-1">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#F3E3FF] text-[#7950A2] text-[11px] font-medium max-w-full truncate">
                        {agent.note}
                      </span>
                    </div>

                    {/* Card Title & Summary */}
                    <h4 className="text-[15px] font-semibold text-[#000000] mb-1.5 px-1">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectAgent(agent.id, true);
                        }}
                        className="text-left hover:underline"
                      >
                        {agent.name} · {agent.job}
                      </button>
                    </h4>
                    <p className="text-xs text-[#323338] leading-relaxed line-clamp-3 px-1 mb-5">
                      {agent.summary}
                    </p>
                  </div>

                  {/* Bottom Row: Tool Icon Badges + Black-Bordered "Get Started ->" Pill */}
                  <div className="pt-2 px-1 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <span className="size-7 rounded-lg bg-[#F3F4F5] text-[#7950A2] flex items-center justify-center">
                        <PhoneCall className="size-3.5" />
                      </span>
                      <span className="size-7 rounded-lg bg-[#F3F4F5] text-[#008A50] flex items-center justify-center">
                        <Calendar className="size-3.5" />
                      </span>
                      <span className="size-7 rounded-lg bg-[#F3F4F5] text-[#181B20] flex items-center justify-center">
                        <FileSpreadsheet className="size-3.5" />
                      </span>
                    </div>

                    <a
                      href={getAuthLink('/register')}
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex min-h-11 items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#181B20] text-xs font-medium text-[#181B20] hover:bg-[#181B20] hover:text-white transition-colors whitespace-nowrap"
                    >
                      <span>Get Started</span>
                      <ArrowRight className="size-3" />
                    </a>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
